import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location, UpperCasePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DATE_FORMAT, MAX_FILE_SIZE } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxNumberBoxModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTagBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorComponent,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import {
    AuthService,
    PermissionType,
    AccessType,
    DataSources,
    InvoicingClient,
    InvoiceDto,
    InvoiceStatus,
    CreateInvoiceCommand,
    UpdateInvoiceCommand,
    InvoiceAction,
    CurrencyCode,
    VendorInfoDto,
    BillToInfoDto,
    EmployeesClient,
    FinancialDocumentItemDto,
} from '@services';
import notify from 'devextreme/ui/notify';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { FileUtils } from 'src/app/shared/file-utils';
import EnumUtils from 'src/app/shared/enum-utils';
import { FormatPhonePipe } from 'src/app/pipes/format-phone.pipe';
import { InvoiceDetailsToolbarComponent } from './toolbar/invoice-details-toolbar.component';
import { FileUploadComponent } from 'src/app/components';
import { InvoiceDetailsItemsComponent } from 'src/app/pages/Invoicing/components/invoice-details-items.component/invoice-details-items.component';
import { InvoiceStatusPipe } from 'src/app/pipes/invoice-status.pipe';
import * as AspNetData from "devextreme-aspnet-data-nojquery";
import DataSource from 'devextreme/data/data_source';
import { NotesComponent, NoteType } from 'src/app/components/library/notes/notes.component';

@Component({
    selector: 'app-invoice-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxNumberBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
        FormatPhonePipe,
        InvoiceDetailsItemsComponent,
        InvoiceDetailsToolbarComponent,
        FileUploadComponent,
        RouterLink,
        InvoiceStatusPipe,
        NotesComponent,
    ],
    providers: [UpperCasePipe],
    templateUrl: './invoice-details.component.html',
})
export class InvoiceDetailsComponent implements OnInit {
    @Input() number: string | null = null;
    @ViewChild('poValidator') poValidator!: DxValidatorComponent;
    @ViewChild('validationGroup', { static: true }) validationGroup!: DxValidationGroupComponent;
    @ViewChild(DxScrollViewComponent, { static: false }) scrollView?: DxScrollViewComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    private auth = inject(AuthService);
    private dataSources = inject(DataSources);
    private invoicingClient = inject(InvoicingClient);
    private employeesClient = inject(EmployeesClient);
    private upperCasePipe = inject(UpperCasePipe);
    
    protected readonly PermissionType = PermissionType;
    protected readonly NoteType = NoteType;

    currencyOptions = EnumUtils.getEnumOptions(CurrencyCode, (v) =>
        this.upperCasePipe.transform(v),
    );

    serviceTypeDataSource = this.dataSources.availableServiceTypeOptions();
    purchaseOrderOptionsDataSource: AspNetData.CustomStore | [] = [];
    vendorDataSource = this.dataSources.activeVendorOptions();
    billToDataSource: DataSource = this.buildBillToDataSource();
    dateFormat = DATE_FORMAT;
    minDueDate = new Date();
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    isExporting: boolean = false;
    maxFileSize: number = MAX_FILE_SIZE;
    newInvoiceFiles: File[] = [];
    newBillOfMaterialsFiles: File[] = [];
    invoiceFilesToRemove: string[] = [];
    billOfMaterialsFilesToRemove: string[] = [];
    isReadOnly = false;
    invoice: InvoiceDto = new InvoiceDto();
    vendor: VendorInfoDto | null = null;
    billTo: BillToInfoDto | null = null;
    term: number = 0;

    // When viewing, feed the Bill To box the stored value so it always renders, even if the current
    // user's site scope would exclude it. Editing uses the scoped remote options.
    private buildBillToDataSource(): DataSource {
        if (this.isReadOnly) {
            return new DataSource({
                key: 'id',
                store: this.invoice?.billToClientId
                    ? [{ id: this.invoice.billToClientId, name: this.invoice.billToName }]
                    : [],
            });
        }

        return new DataSource({
            store: this.dataSources.billToOptions(PermissionType.Invoices),
            paginate: true,
        });
    }
    pendingNoteAction: InvoiceAction | null = null;
    actionsRequiringNote: InvoiceAction[] = [
        InvoiceAction.BackToVendor,
        InvoiceAction.Investigate,
        InvoiceAction.OnHold,
        InvoiceAction.Decline,
        InvoiceAction.InvestigationCompleted,
        InvoiceAction.ResumeInvoice,
        InvoiceAction.BackToL1,
        InvoiceAction.UndoToPendingAP,
    ];

    ngOnInit(): void {
        this.number = this.route.snapshot.paramMap.get('number');
        if (this.number) {
            this.loadInvoice();
        } else {
            this.initializeNewInvoice();
        }
    }

    private initializeNewInvoice() {
        this.invoice = new InvoiceDto();
        this.invoice.items = [FinancialDocumentItemDto.fromJS({ quantity: 1, description: '', unitPrice: 0.00, taxRate: 0 })];
        this.newInvoiceFiles = [];
        this.newBillOfMaterialsFiles = [];
        this.invoiceFilesToRemove = [];
        this.billOfMaterialsFilesToRemove = [];
        this.invoice.purchaseOrderNotFound = false;
        this.invoice.invoiceFiles = []
        this.invoice.billOfMaterialsFiles = []
        this.invoice.subTotal = 0;
        this.invoice.taxAmount = 0;
        this.invoice.totalAmount = 0;
        this.purchaseOrderOptionsDataSource = this.dataSources.invoicePurchaseOrderOptions();
        this.setVendor();
        
        this.isReadOnly = !this.auth.checkPermission(PermissionType.InvoiceDetails, AccessType.Edit);
        this.billToDataSource = this.buildBillToDataSource();
    }

    private loadInvoice() {
        this.isLoading = true;
        this.invoicingClient.getInvoice(this.number!)
            .subscribe({
                next: async (data) => {
                    this.invoice = data;
                    this.newInvoiceFiles = [];
                    this.newBillOfMaterialsFiles = [];
                    this.invoiceFilesToRemove = [];
                    this.billOfMaterialsFilesToRemove = [];
                    this.isLoading = false;
                    this.vendor = { serviceTypeId: data.serviceTypeId } as VendorInfoDto;
                    this.updateVendorInfo(false);
                    this.updateBillToInfo();
                    this.onServiceTypeChanged();
                    this.purchaseOrderOptionsDataSource = this.dataSources.invoicePurchaseOrderOptions(data.purchaseOrderId);

                    const canEdit = this.auth.checkPermission(PermissionType.InvoiceDetails, AccessType.Manage)
                        || (this.invoice.status === InvoiceStatus.Draft && this.auth.checkPermission(PermissionType.InvoiceDetails, AccessType.Edit));
                        
                    this.isReadOnly = !canEdit;
                    this.billToDataSource = this.buildBillToDataSource();
                },
                error: () => {
                    notify('Failed to load invoice', 'error', 3000);
                    this.isLoading = false;
                    this.location.back();
                },
            });
    }

    private setVendor() {
        if(this.auth.userId !== null) {
            this.employeesClient.getProfileVendor(this.auth.userId).subscribe({
                next: (data) => {
                    if(data.vendors?.length == 1){
                        this.invoice.vendorId = data.vendors[0]
                    }
                },
                error: () => {
                    this.isLoading = false;
                    notify('Failed to load vendors', 'error', 3000);
                }
            });
        }
    }

    get canViewNotes(){
        return this.auth.checkPermission(PermissionType.InvoiceNotes, AccessType.ViewOnly);
    }

    get canCreateNotes(){
        return this.auth.checkPermission(PermissionType.InvoiceNotes, AccessType.Manage);
    }

    get canCreateInternalNotes(){
        return this.auth.checkPermission(PermissionType.InvoiceInternalNotes, AccessType.Manage);
    }

    protected getBadgeClass(status: InvoiceStatus | undefined): string {
        return EnumUtils.getInvoiceStatusBadgeClass(status);
    }

    validatePurchaseOrder = (e: { value?: any }): boolean => { 
        if (this.invoice.purchaseOrderNotFound) { return true; } 

        return e.value !== null && e.value !== undefined && e.value !== ''; 
    };

    onPurchaseOrderNotFoundChange($event: any) {
        if($event.value == false) return;

        if(this.invoice.purchaseOrderId)
            this.invoice.items = [FinancialDocumentItemDto.fromJS({ quantity: 1, description: '', unitPrice: 0.00, taxRate: 0 }),];

        this.invoice.purchaseOrderId = undefined;
    }

    protected onPoChanged($event: any) {
        this.invoicingClient.getInvoicePurchaseOrderInfo($event.value)
            .subscribe({
                next: async (data) => {
                    this.invoice.purchaseOrderId = data.id;
                    this.invoice.serviceTypeId = data.serviceTypeId;
                    this.invoice.vendorId = data.vendorId;
                    this.invoice.billToClientId = data.billToClientId;
                    this.vendor = { serviceTypeId: data.serviceTypeId } as VendorInfoDto;
                    this.updateVendorInfo(true);
                    this.onServiceTypeChanged();
                    this.invoice.currency = data.currency;
                    this.invoice.items = data.items!.map(i => {
                        const item = FinancialDocumentItemDto.fromJS(i);
                        item.id = undefined;
                        return item;
                    });
                },
                error: () => {
                    notify('Failed to load po', 'error', 3000);
                    this.isLoading = false;
                    this.location.back();
                },
            });
    }


    protected updateVendorInfo(applyGlCodeDefaults: boolean) {
        if (this.invoice.vendorId) {
            this.invoicingClient.getVendorInfo(this.invoice.vendorId).subscribe({
                next: (vendor) => {
                    this.vendor = vendor;
                    this.term = vendor.termDays ?? 0

                    if(this.invoice.serviceTypeId === undefined) {
                        this.invoice.serviceTypeId = vendor.serviceTypeId;
                    }

                    if(applyGlCodeDefaults && !this.invoice.purchaseOrderId) {
                        this.invoice.items?.forEach(item => {
                            item.glCodeId = vendor.glCodeId;
                            item.glCodeName = vendor.glCodeName;
                            item.siteId = vendor.glCodeSiteId;
                        });
                    }
                },
                error: () => {
                    notify('Failed to load vendor details', 'error', 3000);
                    this.vendor = null;
                },
            });

        } else {
            this.vendor = null;
        }
    }

    protected updateBillToInfo() {
        if (this.invoice.billToClientId) {
            this.invoicingClient.getBillToInfo(this.invoice.billToClientId).subscribe({
                next: (billTo) => {
                    this.billTo = billTo;
                },
                error: () => {
                    this.billTo = null;
                },
            });
        } else {
            this.billTo = null;
        }
    }

    protected onServiceTypeChanged() {
        this.vendorDataSource = this.dataSources.activeVendorOptions(this.invoice.serviceTypeId);
        if(this.vendor?.serviceTypeId !== this.invoice.serviceTypeId) {            
            this.invoice.vendorId = undefined;
        }
    }

    async onDateChanged() {
        if (!this.invoice.issueDate) return;

        this.minDueDate = this.invoice.issueDate;
        
        const dueDate = new Date(this.invoice.issueDate);
        dueDate.setDate(dueDate.getDate() + this.term);
        this.invoice.dueDate = dueDate;

        this.updateCurrency();
    }

    updateCurrency() {
        if (this.invoice.currency == CurrencyCode.USD && this.invoice.issueDate) {
            const conversionDate = new Date(this.invoice.issueDate);
            conversionDate.setMonth(conversionDate.getMonth() - 1);

            this.invoicingClient
                .getConversionRate(conversionDate)
                .subscribe({
                    next: (conversionRate) => {
                        this.invoice.conversionRate = conversionRate;
                    },
                    error: () => {
                        notify(
                            'Failed to load conversion rate',
                            'error',
                            3000,
                        );
                        this.invoice.conversionRate =
                            undefined;
                    },
                });
        } else {
            this.invoice.conversionRate = undefined;
        }
    }

    onSaveClick(action: InvoiceAction) {     
        if(this.actionsRequiringNote.includes(action))
        {
            this.pendingNoteAction = action;
            this.notesComponent.openCreatePopup();

        } else{
            this.save(action);
        }
    }

    onNoteSaved() {
        if (!this.pendingNoteAction)
        {
            return;
        }

        const action = this.pendingNoteAction;
        this.pendingNoteAction = null;
        this.save(action);
    }

    onNoteCanceled() {
        this.pendingNoteAction = null;
    }

    async save(action: InvoiceAction) {
        const result = this.validationGroup.instance.validate();

        if (!result.isValid) {
            notify(
                {
                    message: 'Please fix validation errors',
                    position: 'bottom center',
                },
                'error',
                3000,
            );
            return;
        }

        this.isSubmitting = true;

        const invoiceFiles = await FileUtils.mapFilesToDto(this.newInvoiceFiles);
        const billOfMaterialsFiles = await FileUtils.mapFilesToDto(this.newBillOfMaterialsFiles);

        const command = this.number ?
            new UpdateInvoiceCommand({ 
                ...this.invoice, 
                invoiceFiles, 
                billOfMaterialsFiles, 
                invoiceFilesRemove: this.invoiceFilesToRemove,
                billOfMaterialsFilesRemove: this.billOfMaterialsFilesToRemove,
                number: this.number, 
                action: action}) :
            new CreateInvoiceCommand({ ...this.invoice, invoiceFiles, billOfMaterialsFiles, action: action });

        const request$ = this.number ?
            this.invoicingClient.updateInvoice(command) :
            this.invoicingClient.createInvoice(command);

        request$.subscribe({
            next: async (id) => {
                if (this.number === null && this.notesComponent) {
                    await this.notesComponent.saveQueuedNotes(id);
                }

                notify(
                    this.number
                        ? 'Invoice updated successfully'
                        : 'Invoice created successfully',
                    'success',
                    3000,
                );
                this.isSubmitting = false;
                if (this.number) {
                    this.loadInvoice();
                } else {
                    await this.router.navigate(['invoicing/invoices/details', id], {
                        replaceUrl: true,
                    });
                }
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message ||
                    (this.number
                        ? 'Failed to update invoice'
                        : 'Failed to create invoice');
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            },
        });
    }

    exportPdf() {
        this.isExporting = true;
        this.invoicingClient.exportInvoicePdf(this.number!).subscribe({
            next: (file) => {
                FileUtils.downloadFile(file);
                this.isExporting = false;
            },
            error: () => {
                notify('Failed to export invoice', 'error', 3000);
                this.isExporting = false;
            },
        });
    }

    cancel() {
        this.location.back();
    }
}
